import { memo, useEffect, useMemo } from 'react';
import { FieldErrors, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lazily } from 'react-lazily';

import type { Country } from '@login/helpers/arrays';
import { getCountriesNormalize } from '@login/helpers/options';

const { Insert, Select } = lazily(() => import('core/generic/elements/form'));

export type BirthValues = {
	address: {
		birthCountry: string;
		birthCounty?: string;
		birthCity: string;
	};
};

type ApiErrors = {
	BirthCounty?: string;
	BirthCountry?: string;
	BirthCity?: string;
};

export type BirthPlaceControls = {
	countryOfBirthEnabled?: boolean;
	countyOfBirthEnabled?: boolean;
	cityOfBirthEnabled?: boolean;
};

type ChannelSpecificFields = {
	showCountyOfBirthForSpecifiedCountries: string[];
	showAndRequireCityOfBirth?: boolean;
	showAndRequireCountryOfBirth?: boolean;
	showAndRequireCountyOfBirth?: boolean;
};

type BirthPlaceBlockProps = {
	/** Countries for which a county of birth is also collected. */
	countriesWithCounty: string[];
	countries: Country[];
	errors: FieldErrors<BirthValues>;
	apiErrors?: ApiErrors;
	channelSpecificFields: ChannelSpecificFields;
	defaultValues?: {
		birthCountry: string;
		birthCounty: string;
		birthCity: string;
	};
	controls?: BirthPlaceControls;
};

const ALL_ENABLED: BirthPlaceControls = {
	countryOfBirthEnabled: true,
	countyOfBirthEnabled: true,
	cityOfBirthEnabled: true,
};

const BirthPlaceBlockInner = ({
	errors,
	controls = ALL_ENABLED,
	apiErrors,
	countries,
	defaultValues,
	countriesWithCounty,
	channelSpecificFields,
}: BirthPlaceBlockProps) => {
	const { t } = useTranslation();
	const { control, register, trigger, setValue } = useFormContext();

	const birthCountryValue = useWatch({
		control,
		name: 'address.birthCountry',
	});

	const isCountyDisplayed = useMemo(() => {
		const shortName = countries.find(
			(country) => country.fullName === birthCountryValue
		)?.shortName;

		return !!shortName && countriesWithCounty.includes(shortName);
	}, [birthCountryValue, countries, countriesWithCounty]);

	useEffect(() => {
		if (!defaultValues) return;

		if (defaultValues.birthCountry)
			setValue('address.birthCountry', defaultValues.birthCountry);
		if (defaultValues.birthCounty)
			setValue('address.birthCounty', defaultValues.birthCounty);
		if (defaultValues.birthCity)
			setValue('address.birthCity', defaultValues.birthCity);
	}, [defaultValues, setValue]);

	return (
		<>
			{channelSpecificFields.showAndRequireCountryOfBirth && (
				<Select
					{...register('address.birthCountry', {
						required: t(
							'fullregister.birth_country_required_error_full'
						),
					})}
					name="address.birthCountry"
					label={t('fullregister.birth_country_label_full')}
					control={control}
					trigger={trigger}
					id="address.birthCountry"
					disabled={!controls.countryOfBirthEnabled}
					placeholder={''}
					error={
						errors.address?.birthCountry?.message ||
						apiErrors?.BirthCountry ||
						''
					}
					errorIcon="AlertCircleFilled"
					required={!!controls.countryOfBirthEnabled}
					options={getCountriesNormalize(countries, 'fullName', 'IT')}
				/>
			)}

			{channelSpecificFields.showAndRequireCountyOfBirth &&
				isCountyDisplayed && (
					<Insert
						{...register('address.birthCounty', {
							required: t(
								'fullregister.birth_county_required_error_full'
							),
						})}
						defaultValue={''}
						id="address.birthCounty"
						label={t('fullregister.birth_county_label_full')}
						required={!!controls.countyOfBirthEnabled}
						error={
							errors.address?.birthCounty?.message ||
							apiErrors?.BirthCounty ||
							''
						}
						errorIcon="AlertCircleFilled"
						disabled={!controls.countyOfBirthEnabled}
					/>
				)}

			{channelSpecificFields.showAndRequireCityOfBirth && (
				<Insert
					{...register('address.birthCity', {
						required: t(
							'fullregister.birth_city_required_error_full'
						),
					})}
					defaultValue={''}
					id="address.birthCity"
					label={t('fullregister.birth_city_label_full')}
					required={!!controls.cityOfBirthEnabled}
					error={
						errors.address?.birthCity?.message ||
						apiErrors?.BirthCity ||
						''
					}
					errorIcon="AlertCircleFilled"
					disabled={!controls.cityOfBirthEnabled}
				/>
			)}
		</>
	);
};

export const BirthPlaceBlock = memo(BirthPlaceBlockInner);

export default BirthPlaceBlock;
